import i18n from 'i18n'
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit'
import { suppliersAPI } from 'api/suppliers'
import { isOfflineQueuedFulfilledAction } from 'helpers/offlineAction'
import { branchChanged } from './branchSlice'

interface Supplier {
 id: number
 name: string
 phone: string
 notes?: string
 opening_balance?: string
 current_balance?: string
 purchases_count?: number
 created_at?: string
 updated_at?: string
}

interface SuppliersState {
 data: Supplier[]
 loading: boolean
 error: string | null
}

const initialState: SuppliersState = {
 data: [],
 loading: false,
 error: null,
}

export const fetchSuppliers = createAsyncThunk(
 'suppliers/fetchSuppliers',
 async (params: { search?: string } | undefined, { rejectWithValue }) => {
 try {
 const response = await suppliersAPI.getAll(params)
 return response
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.suppliers.fetchFailed'))
 }
 }
)

export const createSupplier = createAsyncThunk(
 'suppliers/createSupplier',
 async (supplierData: any, { rejectWithValue }) => {
 try {
 const response = await suppliersAPI.create(supplierData)
 const body = response as { data?: Supplier }
 return body.data ?? (response as Supplier)
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.suppliers.createFailed'))
 }
 }
)

export const updateSupplier = createAsyncThunk(
 'suppliers/updateSupplier',
 async ({ id, data }: { id: number; data: any }, { rejectWithValue }) => {
 try {
 const response = await suppliersAPI.update(id, data)
 const supplierData = response.data?.data ?? response.data ?? response
 if (supplierData && !supplierData.id) {
 supplierData.id = id
 }
 return supplierData
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.errors || error.response?.data?.message || i18n.t('errors.suppliers.updateFailed'))
 }
 }
)

export const deleteSupplier = createAsyncThunk(
 'suppliers/deleteSupplier',
 async (id: number, { rejectWithValue }) => {
 try {
 await suppliersAPI.delete(id)
 return id
 } catch (error: any) {
 return rejectWithValue(error.response?.data?.message || i18n.t('errors.suppliers.deleteFailed'))
 }
 }
)

const suppliersSlice = createSlice({
 name: 'suppliers',
 initialState,
 reducers: {
 clearSuppliers: (state) => {
 state.data = []
 state.error = null
 },
 },
 extraReducers: (builder) => {
 builder
 // Fetch suppliers
 .addCase(fetchSuppliers.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(fetchSuppliers.fulfilled, (state, action) => {
 state.loading = false
 const payload = action.payload as any
 const data = Array.isArray(payload?.data) ? payload.data : (Array.isArray(payload) ? payload : [])
 state.data = data.filter((item: any) => item != null)
 })
 .addCase(fetchSuppliers.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Create supplier
 .addCase(createSupplier.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(createSupplier.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const created = action.payload as { id?: number; _offlineOptimistic?: boolean }
 if (created?._offlineOptimistic || created?.id == null) {
 return
 }
 state.data.push(created as any)
 })
 .addCase(createSupplier.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Update supplier
 .addCase(updateSupplier.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(updateSupplier.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 const payload = action.payload as any
 const supplierId = payload?.id
 if (supplierId) {
 const index = state.data.findIndex((sup) => sup.id === supplierId)
 if (index !== -1) {
 state.data[index] = { ...state.data[index], ...payload }
 }
 }
 })
 .addCase(updateSupplier.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 // Delete supplier
 .addCase(deleteSupplier.pending, (state) => {
 state.loading = true
 state.error = null
 })
 .addCase(deleteSupplier.fulfilled, (state, action) => {
 state.loading = false
 if (isOfflineQueuedFulfilledAction(action)) {
 return
 }
 state.data = state.data.filter((sup) => sup.id !== action.payload)
 })
 .addCase(deleteSupplier.rejected, (state, action) => {
 state.loading = false
 state.error = action.payload as string
 })
 .addCase(branchChanged, (state) => {
 Object.assign(state, initialState)
 })
 },
})

export const { clearSuppliers } = suppliersSlice.actions
export default suppliersSlice.reducer
